import { computed, reactive, ref } from 'vue';
import EmailInboxAPI from 'dashboard/api/aceleres/emailInbox';
import wootConstants from 'dashboard/constants/globals';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Aceleres — estado da aba E-mails da Caixa de Entrada.
//
// Fica no nivel do modulo (e nao no Vuex de conversas) de proposito: e-mail nao
// e atendimento, e a lista de conversas tem caches e reconciliacoes que o
// misturariam as caixas de atendimento. O contador de nao lidos tambem vive
// aqui porque o badge do menu lateral precisa dele antes de a aba abrir.

const inboxes = ref([]);
const labels = ref([]);
const threads = ref([]);
const unreadCount = ref(0);
const hasMore = ref(false);
const totalCount = ref(0);
const perPage = ref(50);
const page = ref(1);
const isLoadingInboxes = ref(false);
const isLoadingThreads = ref(false);
const hasLoadedThreads = ref(false);
const loadError = ref(false);
const selectedThreadIds = ref(new Set());
const isBulkActionLoading = ref(false);
const filters = reactive({
  status: 'all',
  folder: 'inbox',
  category: 'primary',
  label: '',
  sort: wootConstants.INBOX_SORT_BY.NEWEST,
  q: '',
  inboxId: null,
});

let listRequestSequence = 0;
let countRequestSequence = 0;
let countedAccountId = null;
let realtimeRefreshTimer = null;
let realtimeSubscribed = false;
let listIsMounted = false;

const currentAccountId = () => window.location.pathname.split('/')[3];

const fetchUnreadCount = async () => {
  const accountId = currentAccountId();
  if (accountId !== countedAccountId) {
    countedAccountId = accountId;
    unreadCount.value = 0;
    threads.value = [];
    inboxes.value = [];
    clearSelection();
  }
  const sequence = ++countRequestSequence;
  try {
    const { data } = await EmailInboxAPI.counts();
    if (sequence !== countRequestSequence || accountId !== currentAccountId())
      return;
    unreadCount.value = Number(data?.unread_count || 0);
  } catch {
    // O badge nunca pode quebrar o menu lateral.
  }
};

const replaceThread = updated => {
  const index = threads.value.findIndex(thread => thread.id === updated.id);
  if (index > -1)
    threads.value.splice(index, 1, { ...threads.value[index], ...updated });
};

const setSelectedThreadIds = ids => {
  selectedThreadIds.value = new Set(ids);
};

const toggleThreadSelected = id => {
  const ids = new Set(selectedThreadIds.value);
  if (ids.has(id)) ids.delete(id);
  else ids.add(id);
  selectedThreadIds.value = ids;
};

const clearSelection = () => setSelectedThreadIds([]);

const applyBulkReadState = (ids, unread) => {
  countRequestSequence += 1;
  const selected = new Set(ids);
  let unreadDelta = 0;

  threads.value = threads.value
    .map(thread => {
      if (!selected.has(thread.id) || thread.unread === unread) return thread;
      unreadDelta += unread ? 1 : -1;
      return { ...thread, unread };
    })
    .filter(thread => {
      if (filters.status === 'unread') return thread.unread;
      if (filters.status === 'read') return !thread.unread;
      return true;
    });

  unreadCount.value = Math.max(0, unreadCount.value + unreadDelta);
};

const bulkMarkRead = async ids => {
  if (!ids.length || isBulkActionLoading.value) return;
  isBulkActionLoading.value = true;
  try {
    await EmailInboxAPI.bulkMarkRead(ids);
    applyBulkReadState(ids, false);
    clearSelection();
    await fetchUnreadCount();
  } finally {
    isBulkActionLoading.value = false;
  }
};

const bulkMarkUnread = async ids => {
  if (!ids.length || isBulkActionLoading.value) return;
  isBulkActionLoading.value = true;
  try {
    await EmailInboxAPI.bulkMarkUnread(ids);
    applyBulkReadState(ids, true);
    clearSelection();
    await fetchUnreadCount();
  } finally {
    isBulkActionLoading.value = false;
  }
};

const toggleThreadStar = async id => {
  const thread = threads.value.find(item => item.id === id);
  const previousStarred = thread?.starred;
  if (thread) replaceThread({ id, starred: !previousStarred });
  try {
    const { data } = await EmailInboxAPI.star(id);
    const starred = Boolean(data?.starred);
    if (thread) replaceThread({ id, starred });
    return starred;
  } catch (error) {
    if (thread) replaceThread({ id, starred: previousStarred });
    throw error;
  }
};

const fetchInboxes = async () => {
  const accountId = currentAccountId();
  const sequence = countRequestSequence;
  isLoadingInboxes.value = true;
  try {
    const { data } = await EmailInboxAPI.inboxes();
    if (accountId !== currentAccountId()) return;
    inboxes.value = data?.data || [];
    if (sequence === countRequestSequence) {
      unreadCount.value = inboxes.value.reduce(
        (total, inbox) => total + Number(inbox.unread_count || 0),
        0
      );
    }
  } catch {
    inboxes.value = [];
  } finally {
    isLoadingInboxes.value = false;
  }
};

const fetchLabels = async () => {
  try {
    const { data } = await EmailInboxAPI.labels();
    labels.value = data?.data || [];
  } catch {
    labels.value = [];
  }
};

const fetchThreads = async ({ append = false, page: requestedPage } = {}) => {
  listRequestSequence += 1;
  const requestId = listRequestSequence;
  const nextPage = append ? page.value + 1 : requestedPage || 1;
  isLoadingThreads.value = true;
  loadError.value = false;

  try {
    const { data } = await EmailInboxAPI.threads({
      page: nextPage,
      status: filters.status,
      folder: filters.folder,
      category: filters.category,
      label: filters.label,
      sort: filters.sort,
      q: filters.q.trim(),
      inboxId: filters.inboxId,
    });
    // Um filtro trocado no meio da requisicao nao pode ser sobrescrito pela
    // resposta antiga.
    if (requestId !== listRequestSequence) return;

    const received = data?.data || [];
    threads.value = append ? [...threads.value, ...received] : received;
    page.value = nextPage;
    totalCount.value = Number(data?.meta?.total_count || 0);
    perPage.value = Number(data?.meta?.per_page || 50);
    hasMore.value = Boolean(data?.meta?.has_more);
    hasLoadedThreads.value = true;
  } catch {
    if (requestId === listRequestSequence) loadError.value = true;
  } finally {
    if (requestId === listRequestSequence) isLoadingThreads.value = false;
  }
};

const loadMore = () => {
  if (!hasMore.value || isLoadingThreads.value) return;
  fetchThreads({ append: true });
};

const goToPage = nextPage => {
  const normalizedPage = Math.max(1, Number(nextPage) || 1);
  if (normalizedPage === page.value || isLoadingThreads.value) return;
  clearSelection();
  fetchThreads({ page: normalizedPage });
};

const setThreadUnread = (id, unread) => {
  countRequestSequence += 1;
  const thread = threads.value.find(item => item.id === id);
  if (thread && thread.unread !== unread) {
    unreadCount.value = Math.max(0, unreadCount.value + (unread ? 1 : -1));
    replaceThread({ id, unread });
  }
  // Um item que nao pertence mais ao filtro sai da lista na hora.
  if (filters.status !== 'all' && thread) {
    const keep = filters.status === 'unread' ? unread : !unread;
    if (!keep) threads.value = threads.value.filter(item => item.id !== id);
  }
};

const markRead = async id => {
  await EmailInboxAPI.markRead(id);
  setThreadUnread(id, false);
  await fetchUnreadCount();
};

const markUnread = async id => {
  await EmailInboxAPI.markUnread(id);
  setThreadUnread(id, true);
  await fetchUnreadCount();
};

const scheduleRealtimeRefresh = () => {
  clearTimeout(realtimeRefreshTimer);
  realtimeRefreshTimer = setTimeout(() => {
    fetchUnreadCount();
    // So recarrega a lista na primeira pagina: rolar para baixo e receber a
    // lista pulando de volta para o topo seria pior que esperar o proximo evento.
    if (listIsMounted && page.value === 1) fetchThreads();
  }, 250);
};

const subscribeRealtime = () => {
  if (realtimeSubscribed) return;
  realtimeSubscribed = true;
  emitter.on(BUS_EVENTS.ACELERES_EMAIL_INBOX_UPDATED, scheduleRealtimeRefresh);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, scheduleRealtimeRefresh);
  window.addEventListener('focus', fetchUnreadCount);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') fetchUnreadCount();
  });
};

export function useEmailInbox() {
  subscribeRealtime();

  const alertInboxes = computed(() =>
    inboxes.value.filter(
      inbox => inbox.reauthorization_required || !inbox.imap_enabled
    )
  );

  return {
    inboxes,
    labels,
    threads,
    unreadCount,
    hasMore,
    totalCount,
    perPage,
    currentPage: page,
    filters,
    isLoadingInboxes,
    isLoadingThreads,
    hasLoadedThreads,
    loadError,
    selectedThreadIds,
    isBulkActionLoading,
    alertInboxes,
    fetchInboxes,
    fetchLabels,
    fetchThreads,
    fetchUnreadCount,
    bulkMarkRead,
    bulkMarkUnread,
    loadMore,
    goToPage,
    markRead,
    markUnread,
    setThreadUnread,
    replaceThread,
    setSelectedThreadIds,
    toggleThreadSelected,
    clearSelection,
    toggleThreadStar,
    setListMounted: mounted => {
      listIsMounted = mounted;
    },
  };
}
